import urlConstants from '~/lib/constants/url.constants';
import iqApi from './api/iqApi';
import { CacheTag } from './CacheTag';
import {
  CareTeamProviderType,
  CareTeamProviderTypeNames,
} from '~/lib/constants/patients.constants';
import { datadogRum } from '@datadog/browser-rum';

export interface GetProviderAssignmentRecommendationsRequest {
  patientId: string;
  providerTypes: string[];
}

/**
 * Outbound mapping: Internal CareTeamProviderType enum → IQ API codes (kebab-case).
 * See IQ_API_CODE_TO_DISPLAY_NAME for the inverse (inbound) mapping.
 *
 * @example
 * CareTeamProviderType.MEDICAL_PROVIDER → 'medical'
 * CareTeamProviderType.MEDICAL_ASSISTANT → 'medical-assistant'
 */
const PROVIDER_TYPE_TO_IQ_API_CODE: Record<string, string> = {
  [CareTeamProviderType.MEDICAL_PROVIDER]: 'medical',
  [CareTeamProviderType.MEDICAL_ASSISTANT]: 'medical-assistant',
  [CareTeamProviderType.THERAPIST]: 'therapist',
  [CareTeamProviderType.PSYCHIATRIST]: 'psychiatrist',
  [CareTeamProviderType.DIETITIAN]: 'dietitian',
  [CareTeamProviderType.PEER_MENTOR]: 'peer-mentor',
  [CareTeamProviderType.FAMILY_MENTOR]: 'family-mentor',
  [CareTeamProviderType.RECOVERY_COACH]: 'recovery-coach',
  [CareTeamProviderType.CAREGIVER_COACH]: 'caregiver-coach',
  [CareTeamProviderType.CARE_NAVIGATOR]: 'care-navigator',
};

/**
 * Inbound mapping: IQ API codes → internal display names.
 * Automatically derived from PROVIDER_TYPE_TO_IQ_API_CODE to ensure consistency.
 * The IQ API returns title-cased versions of the codes (e.g., "Medical", "Therapist").
 * Keys are pre-lowercased for efficient runtime lookup.
 *
 * @example
 * 'medical' → 'Medical Provider'
 * 'therapist' → 'Therapist'
 */
export const IQ_API_CODE_TO_DISPLAY_NAME: Record<string, string> =
  Object.entries(PROVIDER_TYPE_TO_IQ_API_CODE).reduce(
    (acc, [providerType, apiCode]) => {
      acc[apiCode.toLowerCase()] = CareTeamProviderTypeNames[providerType];
      return acc;
    },
    {} as Record<string, string>,
  );

/**
 * Normalizes an IQ API provider type code to the internal display name format.
 * The IQ API returns title-cased versions of request codes (e.g., "Medical" for "medical").
 * This function converts them to display names expected by the frontend (e.g., "Medical Provider").
 *
 * Logs a Datadog error when encountering unknown provider types for observability.
 *
 * @param apiProviderType - Provider type string from IQ API response
 * @returns Normalized display name, or original value if unmapped (with error logged)
 */
export function normalizeIqApiProviderType(apiProviderType: string): string {
  const normalized = apiProviderType.trim().toLowerCase();
  const displayName = IQ_API_CODE_TO_DISPLAY_NAME[normalized];

  if (!displayName) {
    datadogRum.addError(
      new Error(`Unknown IQ API provider type: ${apiProviderType}`),
      {
        extra: {
          rawValue: apiProviderType,
          normalized,
          knownTypes: Object.keys(IQ_API_CODE_TO_DISPLAY_NAME),
        },
        tags: { logFrom: 'normalizeIqApiProviderType' },
      },
    );
    return apiProviderType;
  }

  return displayName;
}

/**
 * Maps internal CareTeamProviderType enum values to IQ API provider type codes.
 *
 * When requesting medical providers, also requests medical-assistant to ensure
 * complete provider team recommendations.
 *
 * @param providerTypes - Internal provider type enum values
 * @returns IQ API provider type codes
 */
export function mapProviderTypesToIqApiCodes(
  providerTypes: string[],
): string[] {
  const iqApiCodes: string[] = [];
  const includesMedicalAssistant = providerTypes.includes(
    CareTeamProviderType.MEDICAL_ASSISTANT,
  );

  // Deduplicate input provider types to prevent duplicate IQ API codes
  const uniqueProviderTypes = [...new Set(providerTypes)];

  for (const type of uniqueProviderTypes) {
    const mappedCode = PROVIDER_TYPE_TO_IQ_API_CODE[type];

    if (mappedCode) {
      iqApiCodes.push(mappedCode);

      // Auto-include medical-assistant when medical provider is requested
      if (
        type === CareTeamProviderType.MEDICAL_PROVIDER &&
        !includesMedicalAssistant
      ) {
        iqApiCodes.push('medical-assistant');
      }
    } else {
      // Fallback: convert spaces to hyphens for unmapped types
      iqApiCodes.push(type.replaceAll(' ', '-'));
    }
  }

  return iqApiCodes;
}

export const providerAssignmentsApi = iqApi.injectEndpoints({
  endpoints: (build) => ({
    getProviderAssignmentRecommendations: build.query<
      GetProviderAssignmentRecommendationsResponse,
      GetProviderAssignmentRecommendationsRequest
    >({
      providesTags: (_result, error, { patientId }) =>
        error
          ? []
          : [
              {
                id: patientId,
                type: CacheTag.ProviderAssignmentRecommendations,
              },
            ],
      query: ({ patientId, providerTypes }) => {
        // The IQ service is hosted independently from the main API gateway
        // to enable independent scaling and deployment.
        // URL validation and authentication are handled by iqBaseQuery.

        // Transform internal provider types to IQ API codes
        const iqApiProviderTypes = mapProviderTypesToIqApiCodes(providerTypes);

        // Use RTK Query params for proper serialization
        return {
          url: urlConstants.iq.getProviderAssignmentRecommendations(patientId),
          params: {
            'provider-type': iqApiProviderTypes.join(','),
          },
        };
      },
      transformResponse: (
        response: GetProviderAssignmentRecommendationsResponse,
      ): GetProviderAssignmentRecommendationsResponse => {
        return {
          ...response,
          assignmentOptions: response.assignmentOptions.map((option) => ({
            ...option,
            providerType: normalizeIqApiProviderType(option.providerType),
          })),
        };
      },
    }),
  }),
});

export const {
  useGetProviderAssignmentRecommendationsQuery,
  useLazyGetProviderAssignmentRecommendationsQuery,
} = providerAssignmentsApi;
